import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Input,
  VStack,
  Spinner,
  InputLeftElement,
  InputGroup,
  Flex,
  Text,
} from "@chakra-ui/react";
import { FolderNode } from "./folderNode";
import { useTranslation } from "react-i18next";
import { SearchIcon } from "lucide-react";
import { Paginate } from "../pagination";
import { PAGINATION_DEFAULTS } from "@root/constants";
import { useMainChatContext } from "@root/context/kps-chat-context";
interface FileSelectDropdownProps {
  folders: FolderItem[] | FoldersData;
  isLoading?: boolean;
  onFileSelect?: (file: DocumentItem | null) => void;
  fetchFolders: (params: any) => void;
  page: number;
  setPage: (page: number) => void;
  folderCount: number;
}
export const FileSelectDropdown: React.FC<FileSelectDropdownProps> = ({
  folders,
  isLoading = false,
  onFileSelect,
  fetchFolders,
  page,
  setPage,
  folderCount,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFile, setSelectedFile] = useState<DocumentItem | null>(null);
  const { t } = useTranslation("common");
  const { selectedProject } = useMainChatContext();
  useEffect(() => {
    if (selectedProject) {
      fetchFolders({
        projectId: selectedProject,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        page: page,
      });
    }
  }, [page]);
  const handleSelect = (file: DocumentItem) => {
    const newValue = selectedFile?.id === file?.id ? null : file;
    setSelectedFile(newValue);
    onFileSelect?.(newValue);
  };
  const folderList: FolderItem[] = useMemo(() => {
    if (Array.isArray((folders as any)?.rows)) {
      return (folders as FoldersData).rows;
    }
    return (folders as FolderItem[]) || [];
  }, [folders]);
  const filteredFolders = useMemo(() => {
    if (!folderList || folderList.length === 0) return [];
    if (!searchQuery.trim()) return folderList;
    const lower = searchQuery.toLowerCase();
    return folderList
      .map((f: FolderItem) => {
        const filteredDocs = f.documents?.filter((d: DocumentItem) =>
          d?.fileName_key?.toLowerCase()?.includes(lower)
        );
        if (
          f?.name?.toLowerCase()?.includes(lower) ||
          filteredDocs?.length > 0
        ) {
          return { ...f, documents: filteredDocs };
        }
        return null;
      })
      .filter(Boolean) as FolderItem[];
  }, [folderList, searchQuery]);
  // Remove the early return - keep spinner inside dropdown container
  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      w="360px"
      bg="white"
      shadow="md"
      mt={6}
      p={2}
      opacity="1"
      zIndex="10"
    >
      {/* Search Input - always visible */}
      <InputGroup mb={2} size="sm">
        <InputLeftElement
          pointerEvents="none"
          h="50px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <SearchIcon size={"15px"} />
        </InputLeftElement>
        <Input
          placeholder={t("projects.searchByFileName")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          borderRadius="md"
          h="44px"
          minH={"44px"}
          isDisabled={isLoading || folderList?.length === 0}
          pl="10"
        />
      </InputGroup>
      {/* Content Area - shows spinner or folders */}
      <VStack
        align="stretch"
        spacing={1}
        maxH="170px"
        minH="100px"
        overflowY="auto"
        sx={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            background: "gray.300",
            borderRadius: "4px",
          },
        }}
      >
        {isLoading ? (
          // Spinner inside the content area
          <Flex
            justify="center"
            align="center"
            h="100px"
            direction="column"
            gap={2}
          >
            <Spinner size="md" color="blue.500" thickness="3px" />
          </Flex>
        ) : filteredFolders?.length > 0 ? (
          filteredFolders.map((folder) => (
            <FolderNode
              key={folder?.id}
              folder={folder}
              selectedFile={selectedFile}
              onSelect={handleSelect}
            />
          ))
        ) : (
          <Flex justify="center" align="center" h="100px">
            <Text color="gray.500" fontSize="sm">
              {t("sidebar.NoFoldersFound")}
            </Text>
          </Flex>
        )}
      </VStack>
      {/* Pagination - hidden while loading */}
      {!isLoading && folderCount > PAGINATION_DEFAULTS.PAGESIZE && (
        <Flex justifyContent="center" mt={2} pb={2}>
          <Paginate
            page={page}
            count={folderCount}
            pageSize={PAGINATION_DEFAULTS.PAGESIZE}
            onPageChange={(page: number) => {
              setPage(page);
            }}
            dropdown={true}
          />
        </Flex>
      )}
    </Box>
  );
};
export default FileSelectDropdown;
